import React, { useRef, useEffect, forwardRef } from 'react';
import gsap from 'gsap';

import scene1 from '../../../public/assets/tower/1-Scene.svg';
import scene2 from '../../../public/assets/tower/2-scene.svg';
import scene3 from '../../../public/assets/tower/3-scene.svg';
import scene4 from '../../../public/assets/tower/4-scene.svg';
import scene5 from '../../../public/assets/tower/5-scene.svg';
import sceneFinal from '../../../public/assets/tower/Final.svg';

// Construction Stage Components
const ConstructionStage1 = forwardRef((props, ref) => (
  <img
    ref={ref}
    src={scene1}
    alt="Construction Stage 1 - Foundation"
    className="absolute w-full h-full object-contain -bottom-0 z-10 opacity-20"
    style={{
      objectPosition: "center bottom",
      maskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, black 40%, transparent 100%)",
      WebkitMaskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, black 40%, transparent 100%)",
      ...props.style,
    }}
    onError={(e) => console.error("❌ Scene 1 failed to load:", e)}
    {...props}
  />
));

const ConstructionStage2 = forwardRef((props, ref) => (
  <img
    ref={ref}
    src={scene2}
    alt="Construction Stage 2 - Early Progress"
    className="absolute w-full h-full object-contain -bottom-0 z-10 opacity-0"
    style={{
      objectPosition: "center bottom",
      maskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, black 40%, transparent 100%)",
      WebkitMaskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, black 40%, transparent 100%)",
      ...props.style,
    }}
    onError={(e) => console.error("❌ Scene 2 failed to load:", e)}
    {...props}
  />
));

const ConstructionStage3 = forwardRef((props, ref) => (
  <img
    ref={ref}
    src={scene3}
    alt="Construction Stage 3 - Mid Progress"
    className="absolute w-full h-full object-contain -bottom-0 z-10 opacity-0"
    style={{
      objectPosition: "center bottom",
      maskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, black 40%, transparent 100%)",
      WebkitMaskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, black 40%, transparent 100%)",
      ...props.style,
    }}
    onError={(e) => console.error("❌ Scene 3 failed to load:", e)}
    {...props}
  />
));

const ConstructionStage4 = forwardRef((props, ref) => (
  <img
    ref={ref}
    src={scene4}
    alt="Construction Stage 4 - Advanced Progress"
    className="absolute w-full h-full object-contain -bottom-0 z-10 opacity-0"
    style={{
      objectPosition: "center bottom",
      maskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, black 40%, transparent 100%)",
      WebkitMaskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, black 40%, transparent 100%)",
      ...props.style,
    }}
    onError={(e) => console.error("❌ Scene 4 failed to load:", e)}
    {...props}
  />
));

const ConstructionStage5 = forwardRef((props, ref) => (
  <img
    ref={ref}
    src={scene5}
    alt="Construction Stage 5 - Near Complete"
    className="absolute w-full h-full object-contain -bottom-0 z-10 opacity-0"
    style={{
      objectPosition: "center bottom",
      maskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, black 40%, transparent 100%)",
      WebkitMaskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, black 40%, transparent 100%)",
      ...props.style,
    }}
    onError={(e) => console.error("❌ Scene 5 failed to load:", e)}
    {...props}
  />
));

const ConstructionStage6 = forwardRef((props, ref) => (
  <img
    ref={ref}
    src={sceneFinal}
    alt="Construction Stage 6 - Final Complete Tower"
    className="absolute w-full h-full object-contain -bottom-0 z-10 opacity-0"
    style={{
      objectPosition: "center bottom",
      maskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, black 40%, transparent 100%)",
      WebkitMaskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, black 40%, transparent 100%)",
      ...props.style,
    }}
    onError={(e) => console.error("❌ Final Scene failed to load:", e)}
    {...props}
  />
));

const ParallaxTower = ({ activeIndex = 0 }) => {
  // Layer refs
  const stage1Ref = useRef(null);
  const stage2Ref = useRef(null);
  const stage3Ref = useRef(null);
  const stage4Ref = useRef(null);
  const stage5Ref = useRef(null);
  const stage6Ref = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const stages = [
        stage1Ref.current,
        stage2Ref.current,
        stage3Ref.current,
        stage4Ref.current,
        stage5Ref.current,
        stage6Ref.current
      ];

      // Hide all stages first
      gsap.to(stages, { opacity: 0, duration: 0.5, ease: "power2.inOut" });

      // Calculate which stage to show based on activeIndex
      // We have 6 stages. Let's map activeIndex to a stage.
      // activeIndex will be 0, 1, 2, 3 (for 4 badges).
      
      // Let's create a smooth transition mapping for 5 slides.
      // 0 (Hero)    -> Stage 1 (0)
      // 1 (About)   -> Stage 3 (2)
      // 2 (Work)    -> Stage 4 (3)
      // 3 (Payment) -> Stage 5 (4)
      // 4 (Contact) -> Stage 6 (5)
      
      const exactMap = {
        0: 0, // stage 1
        1: 2, // stage 3
        2: 3, // stage 4
        3: 4, // stage 5
        4: 5  // stage 6
      };

      const realIndex = exactMap[activeIndex] !== undefined ? exactMap[activeIndex] : 0;

      // Show the target stage
      if (stages[realIndex]) {
        gsap.to(stages[realIndex], { opacity: 1, duration: 0.8, ease: "power2.inOut", delay: 0.2 });
      }
      
      // Add subtle vertical shift based on index (simulating parallax depth)
      gsap.to(stages, {
        y: activeIndex * 15,
        duration: 0.8,
        ease: "power2.out"
      });

    });

    return () => ctx.revert();
  }, [activeIndex]);

  return (
    <div
      className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none"
      style={{
        background: "#023666",
        zIndex: 0,
      }}
    >
      {/* === LAYER STACK === */}
      <ConstructionStage1 ref={stage1Ref} />
      <ConstructionStage2 ref={stage2Ref} />
      <ConstructionStage3 ref={stage3Ref} />
      <ConstructionStage4 ref={stage4Ref} />
      <ConstructionStage5 ref={stage5Ref} />
      <ConstructionStage6 ref={stage6Ref} />

      {/* Ground line */}
      <div
        className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-slate-400 to-transparent"
        style={{ zIndex: 25 }}
      ></div>
    </div>
  );
};

export default ParallaxTower;
